import { Check, Close, DocumentScanner, HighlightOff, Radar } from '@mui/icons-material'
import WarningIcon from '@mui/icons-material/Warning'
import { LoadingButton } from '@mui/lab'
import { Button, Card, CardContent, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, TextField, Typography } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { GreenText, LabelField, UnsavedChangesPrompt } from 'components'
import * as controllers from 'controllers'
import { DocumentDto, DocumentTypeDto } from 'dtos'
import { Form, Formik, FormikHelpers } from 'formik'
import { siMICHIGAN_TITLE, siOHIO_TITLE } from 'localConstants/documentTypes'
import moment from 'moment'
import { enqueueSnackbar } from 'notistack'
import PDFObject from 'pdfobject'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { notNullOrUndefined } from 'utils'

const ODOMETER_MISMATCH_WARNING_MESSAGE =
  'Title odometer does not match Vehicle Check-In odometer value. Check with Admin Team on how to proceed with this record.'

function isTitleOdometerDocumentType(documentType?: DocumentTypeDto) {
  return documentType?.specialIdentifier === siMICHIGAN_TITLE || documentType?.specialIdentifier === siOHIO_TITLE
}

function formatTitleOdometerDisplay(value?: string) {
  if (!value) {
    return ''
  }

  const trimmed = value.trim()

  if (!/^\d+$/.test(trimmed)) {
    return trimmed
  }

  return String(parseInt(trimmed, 10))
}

function normalizeOdometerForComparison(value?: string | number | null) {
  if (value === undefined || value === null || value === '') {
    return undefined
  }

  const normalized = Number(String(value).trim())

  return Number.isFinite(normalized) ? normalized : undefined
}

function hasOdometerMismatch(titleOdometer?: string, vehicleCheckInOdometerReadingMi?: number) {
  const normalizedTitleOdometer = normalizeOdometerForComparison(titleOdometer)
  const normalizedVehicleCheckInOdometer = normalizeOdometerForComparison(vehicleCheckInOdometerReadingMi)

  if (normalizedVehicleCheckInOdometer === undefined || normalizedTitleOdometer === undefined) {
    return true
  }

  return normalizedTitleOdometer !== normalizedVehicleCheckInOdometer
}

export default function OCRDocumentGuidIndex() {
  const navigate = useNavigate()
  const { documentGuid } = useParams()

  const [document, setDocument] = useState<DocumentDto>(new DocumentDto())
  const [documentTypes, setDocumentTypes] = useState<DocumentTypeDto[]>([])
  const [vehicleCheckInOdometerReadingMi, setVehicleCheckInOdometerReadingMi] = useState<number | undefined>(undefined)
  const [isLoadingVehicleCheckInOdometer, setIsLoadingVehicleCheckInOdometer] = useState<boolean>(false)
  const [odometerMismatchDialogOpen, setOdometerMismatchDialogOpen] = useState<boolean>(false)
  const [pendingAcceptValues, setPendingAcceptValues] = useState<DocumentDto | null>(null)
  const [isConfirmingOdometerMismatchAccept, setIsConfirmingOdometerMismatchAccept] = useState<boolean>(false)
  const [isReparsing, setIsReparsing] = useState<boolean>(false)
  const pendingFormikHelpersRef = useRef<FormikHelpers<DocumentDto> | null>(null)

  const acceptOCRDocument = useCallback(
    (values: DocumentDto, formikHelpers: FormikHelpers<DocumentDto>) => {
      return controllers.ocr
        .acceptOCRDocument(values)
        .then(() => {
          formikHelpers.resetForm()
          enqueueSnackbar('Document Accepted Successfully!', { variant: 'success' })
          setTimeout(() => {
            navigate('/ocr', { replace: true })
          }, 100)
        })
        .catch(() => {})
        .finally(() => {
          formikHelpers.setSubmitting(false)
        })
    },
    [navigate]
  )

  const closeOdometerMismatchDialog = useCallback(() => {
    setOdometerMismatchDialogOpen(false)
    setPendingAcceptValues(null)
    setIsConfirmingOdometerMismatchAccept(false)
    pendingFormikHelpersRef.current?.setSubmitting(false)
    pendingFormikHelpersRef.current = null
  }, [])

  const confirmOdometerMismatchAccept = useCallback(() => {
    if (!pendingAcceptValues || !pendingFormikHelpersRef.current) {
      return
    }

    const formikHelpers = pendingFormikHelpersRef.current
    formikHelpers.setSubmitting(true)
    setIsConfirmingOdometerMismatchAccept(true)

    acceptOCRDocument(pendingAcceptValues, formikHelpers).finally(() => {
      setOdometerMismatchDialogOpen(false)
      setPendingAcceptValues(null)
      setIsConfirmingOdometerMismatchAccept(false)
      pendingFormikHelpersRef.current = null
    })
  }, [acceptOCRDocument, pendingAcceptValues])

  const loadVehicleCheckInOdometerByVin = useCallback((vin?: string) => {
    const trimmedVin = vin?.trim()

    if (!trimmedVin) {
      setVehicleCheckInOdometerReadingMi(undefined)
      setIsLoadingVehicleCheckInOdometer(false)
      return Promise.resolve()
    }

    setIsLoadingVehicleCheckInOdometer(true)

    return controllers
      .getVehicleByVIN(trimmedVin)
      .then(response => {
        if (response.value?.vehicleGuid) {
          return controllers.getVehicleCheckInDetails(response.value.vehicleGuid)
        }

        setVehicleCheckInOdometerReadingMi(undefined)
        return undefined
      })
      .then(checkInResponse => {
        if (checkInResponse) {
          setVehicleCheckInOdometerReadingMi(checkInResponse.value.vehicleCheckInOdometerReadingMi)
        }
      })
      .catch(() => {
        setVehicleCheckInOdometerReadingMi(undefined)
      })
      .finally(() => {
        setIsLoadingVehicleCheckInOdometer(false)
      })
  }, [])

  useEffect(() => {
    controllers.ocr
      .getDocumentTypes()
      .then(response => {
        const sortedDocumentTypes = response.value.sort((a, b) => {
          if (a.name === '7501') return -1
          if (b.name === '7501') return 1
          return (a.name ?? '').localeCompare(b.name ?? '')
        })
        setDocumentTypes(sortedDocumentTypes)
      })
      .catch(() => {})
      .finally(() => {})
  }, [])

  useEffect(() => {
    if (!documentGuid) {
      return
    }

    controllers.ocr
      .getOCRDocument(documentGuid)
      .then(response => {
        const loadedDocument = response.value

        if (isTitleOdometerDocumentType(loadedDocument.documentType)) {
          setIsLoadingVehicleCheckInOdometer(true)

          if (loadedDocument.keyField2) {
            loadedDocument.keyField2 = formatTitleOdometerDisplay(loadedDocument.keyField2)
          }
        }

        setDocument(loadedDocument)
      })
      .catch(() => {})
      .finally(() => {})
  }, [documentGuid])

  useEffect(() => {
    if (!isTitleOdometerDocumentType(document.documentType)) {
      setVehicleCheckInOdometerReadingMi(undefined)
      setIsLoadingVehicleCheckInOdometer(false)
      return
    }

    setIsLoadingVehicleCheckInOdometer(true)
    loadVehicleCheckInOdometerByVin(document.keyField1)
  }, [document, loadVehicleCheckInOdometerByVin])

  useEffect(() => {
    if (!document.documentGuid) {
      return
    }

    const div = window.document.getElementById(document.documentGuid)

    if (!div) {
      return
    }

    PDFObject.embed(`/api/ocr/${encodeURIComponent(document.documentGuid)}/file`, div, {
      width: '100%',
      height: '100%'
    })
  }, [document])

  return (
    <Formik
      enableReinitialize
      initialValues={document}
      onSubmit={(values, formikHelpers) => {
        if (
          isTitleOdometerDocumentType(values.documentType) &&
          !isLoadingVehicleCheckInOdometer &&
          hasOdometerMismatch(values.keyField2, vehicleCheckInOdometerReadingMi)
        ) {
          setPendingAcceptValues(values)
          pendingFormikHelpersRef.current = formikHelpers
          setOdometerMismatchDialogOpen(true)
          formikHelpers.setSubmitting(false)
          return
        }

        acceptOCRDocument(values, formikHelpers)
      }}
      validateOnBlur
      validateOnChange
      // Validation is handled on backend.
      // validationSchema={}
    >
      {({ dirty, errors, handleBlur, handleChange, handleSubmit, isSubmitting, resetForm, setFieldValue, touched, values }) => (
        <Form>
          <Card>
            <CardContent>
              <Grid container spacing={1} alignItems='flex-start'>
                <Grid item xs={12}>
                  <Typography fontWeight='bold' variant='h5'>
                    OCR Verification
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={4} container spacing={1} alignItems='center'>
                  <Grid item xs={12}>
                    <TextField
                      disabled={isSubmitting}
                      error={Boolean(touched.documentType && errors.documentType)}
                      fullWidth
                      helperText={touched.documentType && errors.documentType}
                      label='Document Type'
                      name='documentType'
                      onBlur={handleBlur}
                      onChange={e => {
                        setFieldValue('documentType', JSON.parse(e.target.value))
                        setFieldValue('keyField1', undefined)
                        setFieldValue('keyField2', undefined)
                        setFieldValue('keyField3', undefined)
                        setFieldValue('keyField4', undefined)
                        setFieldValue('keyField5', undefined)
                        setFieldValue('keyField6', undefined)
                        setVehicleCheckInOdometerReadingMi(undefined)
                      }}
                      select
                      value={values.documentType ? JSON.stringify(values.documentType) : ''}
                    >
                      {documentTypes.map(documentType => (
                        <MenuItem key={documentType.documentTypeGuid} value={JSON.stringify(documentType)}>
                          {documentType.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant='body1' pl={0.5}>
                      {document.displayFileName}
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <GreenText variant='outlined'>
                      {`On the OCR Verification screen, if VTS2 was able to detect the type of document that was uploaded and OCR’d the “Document
Type” dropdown will show the type of document that was detected, and otherwise “Unknown” will be displayed. Users may
modify the document type using this dropdown. If a user changes the selected document type, any OCR’d data field values are
lost/deleted.

The “Key Fields” on the right-hand side of the screen are input fields which are populated based on data that has been OCR’d
from the uploaded document. Users may modify the contents of these fields before saving. The fields that are available change
depending on the document type. If “Unknown” document type is selected, no “Key Fields” (input fields) will be visible.

Pressing Accept will add the OCR’d document to the relevant VIN(s) under their respective “Documents” tabs and returns the
user to the OCR list screen. Pressing “Cancel” returns the user to the OCR list screen without saving changes. Pressing “Delete”
removes the Uploaded document from VTS2 completely and returns the user to the OCR list screen`}
                    </GreenText>
                  </Grid>
                </Grid>

                {values.documentType?.allowReparse && (
                  <Grid item xs={12} sm='auto'>
                    <LoadingButton
                      disabled={isSubmitting}
                      endIcon={<DocumentScanner />}
                      loading={isReparsing}
                      onClick={() => {
                        if (!document.documentGuid || !values.documentType?.documentTypeGuid) {
                          return
                        }

                        setIsReparsing(true)
                        controllers.ocr
                          .reparseOCRDocument({
                            documentGuid: document.documentGuid,
                            documentTypeGuid: values.documentType.documentTypeGuid
                          })
                          .then(response => {
                            setDocument(response.value)
                            resetForm({ values: response.value })
                            enqueueSnackbar('Document Reparsed Successfully!', { variant: 'success' })
                          })
                          .catch(() => {})
                          .finally(() => {
                            setIsReparsing(false)
                          })
                      }}
                      variant='text'
                    >
                      RESCAN
                    </LoadingButton>
                  </Grid>
                )}

                <Grid item xs={12} sm={9}>
                  <div id={document.documentGuid} style={{ width: '100%', height: '600px' }}></div>
                </Grid>

                <Grid item xs={12} sm={3} container spacing={48}>
                  <Grid item xs={true} container spacing={1} alignItems='center' justifyContent='flex-start'>
                    {values.documentType?.keyField1Label &&
                      (values.documentType.keyField1Label.includes('Date') ? (
                        <Grid item xs={12}>
                          <DatePicker
                            disabled={isSubmitting}
                            label={values.documentType.keyField1Label}
                            onChange={newValue => {
                              if (newValue) {
                                setFieldValue('keyField1', newValue.format('yyyy-MM-DD'))
                              } else {
                                setFieldValue('keyField1', undefined)
                              }
                            }}
                            slotProps={{
                              actionBar: {
                                actions: ['clear']
                              },
                              textField: {
                                error: Boolean(touched.keyField1 && errors.keyField1),
                                helperText: touched.keyField1 && errors.keyField1
                              }
                            }}
                            value={values.keyField1 ? moment(values.keyField1) : null}
                          />
                        </Grid>
                      ) : (
                        <Grid item xs={12}>
                          <TextField
                            disabled={isSubmitting}
                            error={Boolean(touched.keyField1 && errors.keyField1)}
                            fullWidth
                            helperText={touched.keyField1 && errors.keyField1}
                            label={values.documentType.keyField1Label}
                            name='keyField1'
                            onBlur={handleBlur}
                            onChange={handleChange}
                            type={values.documentType?.keyField1Label.includes('Date') ? 'date' : 'text'}
                            value={values.keyField1 || ''}
                          />
                        </Grid>
                      ))}

                    {values.documentType?.keyField2Label &&
                      !isTitleOdometerDocumentType(values.documentType) &&
                      (values.documentType.keyField2Label.includes('Date') ? (
                        <Grid item xs={12}>
                          <DatePicker
                            disabled={isSubmitting}
                            label={values.documentType.keyField2Label}
                            onChange={newValue => {
                              if (newValue) {
                                setFieldValue('keyField2', newValue.format('yyyy-MM-DD'))
                              } else {
                                setFieldValue('keyField2', undefined)
                              }
                            }}
                            slotProps={{
                              actionBar: {
                                actions: ['clear']
                              },
                              textField: {
                                error: Boolean(touched.keyField2 && errors.keyField2),
                                helperText: touched.keyField2 && errors.keyField2
                              }
                            }}
                            value={values.keyField2 ? moment(values.keyField2) : null}
                          />
                        </Grid>
                      ) : (
                        <Grid item xs={12}>
                          <TextField
                            disabled={isSubmitting}
                            error={Boolean(touched.keyField2 && errors.keyField2)}
                            fullWidth
                            helperText={touched.keyField2 && errors.keyField2}
                            label={values.documentType.keyField2Label}
                            name='keyField2'
                            onBlur={handleBlur}
                            onChange={handleChange}
                            value={values.keyField2 || ''}
                          />
                        </Grid>
                      ))}

                    {isTitleOdometerDocumentType(values.documentType) && (
                      <>
                        <Grid item xs={12}>
                          <TextField
                            disabled={isSubmitting}
                            error={Boolean(touched.keyField2 && errors.keyField2)}
                            fullWidth
                            helperText={touched.keyField2 && errors.keyField2}
                            label='Title Odometer'
                            name='keyField2'
                            onBlur={e => {
                              handleBlur(e)
                              setFieldValue('keyField2', formatTitleOdometerDisplay(e.target.value) || undefined)
                            }}
                            onChange={handleChange}
                            value={formatTitleOdometerDisplay(values.keyField2)}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <LabelField
                            isLoading={isLoadingVehicleCheckInOdometer}
                            label='Vehicle Check-In Odometer'
                            value={notNullOrUndefined(vehicleCheckInOdometerReadingMi) ? `${vehicleCheckInOdometerReadingMi} MI` : undefined}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <LoadingButton
                            color='error'
                            disabled={isSubmitting}
                            endIcon={<Radar />}
                            fullWidth
                            loading={isLoadingVehicleCheckInOdometer}
                            onClick={() => {
                              loadVehicleCheckInOdometerByVin(values.keyField1)
                            }}
                            variant='text'
                          >
                            Rescan Vehicle Check-In Odometer for VIN
                          </LoadingButton>
                        </Grid>
                      </>
                    )}

                    {values.documentType?.keyField3Label && (
                      <Grid item xs={12}>
                        <TextField
                          disabled={isSubmitting}
                          error={Boolean(touched.keyField3 && errors.keyField3)}
                          fullWidth
                          helperText={touched.keyField3 && errors.keyField3}
                          label={values.documentType.keyField3Label}
                          name='keyField3'
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.keyField3 || ''}
                        />
                      </Grid>
                    )}

                    {values.documentType?.keyField4Label &&
                      (values.documentType.keyField4Label.includes('Date') ? (
                        <Grid item xs={12}>
                          <DatePicker
                            disabled={isSubmitting}
                            label={values.documentType.keyField4Label}
                            onChange={newValue => {
                              if (newValue) {
                                setFieldValue('keyField4', newValue.format('yyyy-MM-DD'))
                              } else {
                                setFieldValue('keyField4', undefined)
                              }
                            }}
                            slotProps={{
                              actionBar: {
                                actions: ['clear']
                              },
                              textField: {
                                error: Boolean(touched.keyField4 && errors.keyField4),
                                helperText: touched.keyField4 && errors.keyField4
                              }
                            }}
                            value={values.keyField4 ? moment(values.keyField4) : null}
                          />
                        </Grid>
                      ) : (
                        <Grid item xs={12}>
                          <TextField
                            disabled={isSubmitting}
                            error={Boolean(touched.keyField4 && errors.keyField4)}
                            fullWidth
                            helperText={touched.keyField4 && errors.keyField4}
                            label={values.documentType.keyField4Label}
                            name='keyField4'
                            onBlur={handleBlur}
                            onChange={handleChange}
                            value={values.keyField4 || ''}
                          />
                        </Grid>
                      ))}

                    {values.documentType?.keyField5Label && (
                      <Grid item xs={12}>
                        <TextField
                          disabled={isSubmitting}
                          error={Boolean(touched.keyField5 && errors.keyField5)}
                          fullWidth
                          helperText={touched.keyField5 && errors.keyField5}
                          label={values.documentType.keyField5Label}
                          name='keyField5'
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.keyField5 || ''}
                        />
                      </Grid>
                    )}

                    {values.documentType?.keyField6Label && (
                      <Grid item xs={12}>
                        <TextField
                          disabled={isSubmitting}
                          error={Boolean(touched.keyField6 && errors.keyField6)}
                          fullWidth
                          helperText={touched.keyField6 && errors.keyField6}
                          label={values.documentType.keyField6Label}
                          name='keyField6'
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.keyField6 || ''}
                        />
                      </Grid>
                    )}
                  </Grid>

                  <Grid item xs={12} container spacing={1} alignItems='center' justifyContent='flex-end'>
                    <Grid item xs={12}>
                      <LoadingButton color='primary' endIcon={<Check />} fullWidth loading={isSubmitting} type='submit' variant='contained'>
                        ACCEPT
                      </LoadingButton>
                    </Grid>

                    <Grid item xs={12}>
                      <Button
                        color='inherit'
                        disabled={isSubmitting}
                        endIcon={<Close />}
                        fullWidth
                        onClick={() => {
                          navigate(-1)
                        }}
                        variant='text'
                      >
                        CANCEL
                      </Button>
                    </Grid>

                    <Grid item xs={12}>
                      <Button
                        color='error'
                        disabled={isSubmitting}
                        endIcon={<HighlightOff />}
                        fullWidth
                        onClick={() => {
                          controllers.ocr.deleteOCRDocument(document.documentGuid!).then(() => {
                            enqueueSnackbar('OCR Document Deleted Successfully!', { variant: 'success' })
                            navigate('/ocr', { replace: true })
                          })
                        }}
                        variant='text'
                      >
                        DELETE
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <GreenText variant='outlined'>{`${values.documentType?.helpfulInformation}`}</GreenText>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <UnsavedChangesPrompt when={dirty} />

          <Dialog open={odometerMismatchDialogOpen} onClose={closeOdometerMismatchDialog}>
            <DialogTitle sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
              <WarningIcon color='warning' />
              Confirm Accept
            </DialogTitle>
            <DialogContent>
              <Typography variant='body1'>{ODOMETER_MISMATCH_WARNING_MESSAGE}</Typography>
            </DialogContent>
            <DialogActions>
              <Button color='inherit' disabled={isConfirmingOdometerMismatchAccept} endIcon={<Close />} onClick={closeOdometerMismatchDialog} variant='text'>
                Cancel
              </Button>
              <LoadingButton
                color='primary'
                disabled={isConfirmingOdometerMismatchAccept}
                endIcon={<Check />}
                loading={isConfirmingOdometerMismatchAccept}
                onClick={confirmOdometerMismatchAccept}
                variant='contained'
              >
                Accept
              </LoadingButton>
            </DialogActions>
          </Dialog>
        </Form>
      )}
    </Formik>
  )
}
